import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    afterNextRender,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import type { Rive as RiveInstance } from '@rive-app/canvas';
import { AppSessionService } from 'src/app/shared/services/app-session.service';
import { I18nService } from 'src/app/shared/services/i18n.service';
import { paintDocumentStack } from './splash-palette';

/**
 * A tela que cobre a web enquanto a primeira carga não volta.
 *
 * No centro, três folhas se revezando: o relatório, o contrato e o orçamento,
 * cada um na cor do seu tipo. O desenho é um arquivo do Rive
 * (`public/rive/document-stack.riv`), recolorido nos bytes antes de abrir, pelo
 * `splash-palette` (o porquê está lá).
 *
 * Quanto ela fica no ar depende de quanto tempo o usuário passou fora:
 *
 * - Voltou depois de uma hora ou mais (ou é o primeiro acesso): a tela cumpre
 *   `MIN_DURATION_MS` inteiro, mesmo que a carga volte antes. É a abertura do
 *   app, e ela merece ser vista.
 * - Voltou antes disso (refresh, aba nova, voltar do navegador): ela sai assim
 *   que a carga volta. Ninguém quer ver quatro segundos de animação por ter
 *   apertado F5.
 *
 * `MAX_DURATION_MS` fecha a conta quando a carga não volta nunca: API pendurada
 * não pode prender a tela para sempre.
 *
 * A carga começa no mesmo instante em que esta tela aparece, e não depois dela:
 * o tempo mínimo é um piso para a animação, nunca uma espera somada à rede.
 */

/** O piso, quando a volta conta como chegada. */
const MIN_DURATION_MS = 5000;

/** O teto, para o caso de a carga não voltar. */
const MAX_DURATION_MS = 12000;

/** O mesmo tempo da transição de opacidade no .scss. */
const EXIT_MS = 320;

/** De quanto em quanto tempo a folha da frente troca. */
const SHEET_INTERVAL_MS = 1150;

/** A chave da frase que se forma embaixo das folhas. */
const HINT_KEY = 'core.splash.opening';

/** Quanto uma letra leva para surgir. O mesmo tempo da animação no .scss. */
const LETTER_MS = 500;

/**
 * E quanto a frase inteira leva para se formar, da primeira letra ao fim da
 * última. É daqui que sai o intervalo entre uma letra e a próxima, em vez de um
 * atraso escolhido no olho: trocar a frase (ou o idioma) não muda o tempo dela.
 */
const PHRASE_MS = 3000;

/**
 * O nome da máquina de estados dentro do arquivo. Sem ele o runtime toca a
 * animação solta do artboard, ela termina, e a pilha congela na primeira folha.
 */
const STATE_MACHINE = 'State Machine 1';

/**
 * O gatilho que joga a folha da frente para trás. É a única entrada que o
 * arquivo publica: a posição da pilha não é propriedade que se escreva, então
 * revezar as folhas é disparar isto no compasso.
 */
const NEXT_SHEET_INPUT = 'Pressed';

@Component({
    selector: 'splash-screen',
    standalone: true,
    templateUrl: './splash-screen.component.html',
    styleUrl: './splash-screen.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.is-leaving]': 'leaving()' },
})
export class SplashScreenComponent {
    /** A primeira carga terminou. Falha também conta: o erro tem o toast dele. */
    public ready = input.required<boolean>();

    /** A tela sumiu e pode ser desmontada. */
    public done = output<void>();

    private _destroyRef = inject(DestroyRef);
    private _session = inject(AppSessionService);
    private _i18n = inject(I18nService);

    private _stage = viewChild.required<ElementRef<HTMLCanvasElement>>('stage');

    /**
     * A frase, quando o dicionário já respondeu.
     *
     * Enquanto ele não chega, `t()` devolve a própria chave, e é isso que o
     * vazio evita: esta tela nasce antes da primeira carga, e sem o teste a
     * abertura mostraria `core.splash.opening` soletrado letra a letra.
     */
    protected readonly phrase = computed(() => {
        const text = this._i18n.t(HINT_KEY);

        return text === HINT_KEY ? '' : text;
    });

    /** A frase em letras, para elas entrarem uma a uma. */
    protected readonly letters = computed(() => [...this.phrase()]);

    /** O intervalo entre o começo de uma letra e o da seguinte. */
    protected readonly letterDelay = computed(
        () => (PHRASE_MS - LETTER_MS) / Math.max(1, this.letters().length - 1),
    );

    protected leaving = signal(false);

    /** O arquivo chegou e o primeiro quadro já tem o que mostrar. */
    protected drawing = signal(false);

    private readonly _bornAt = performance.now();

    /**
     * Quem pediu menos movimento não ganha a animação nem o piso: a tela vira um
     * fundo com o logotipo, e sai assim que a carga volta.
     */
    private readonly _reducedMotion =
        typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    private readonly _minDuration =
        !this._reducedMotion && this._session.isReturningAfterLongAway() ? MIN_DURATION_MS : 0;

    private _timers: ReturnType<typeof setTimeout>[] = [];
    private _rive: RiveInstance | null = null;
    private _exitScheduled = false;
    private _destroyed = false;

    constructor() {
        this._destroyRef.onDestroy(() => {
            this._destroyed = true;
            this._timers.forEach(clearTimeout);
            this._rive?.cleanup();
        });

        if (!this._reducedMotion) afterNextRender(() => void this._open());

        // A saída é a última das duas: a carga que volta e o piso no ar.
        effect(() => {
            if (!this.ready()) return;
            this._scheduleExit(Math.max(0, this._minDuration - (performance.now() - this._bornAt)));
        });

        this._timers.push(setTimeout(() => this._scheduleExit(0), MAX_DURATION_MS));
    }

    private async _open(): Promise<void> {
        // O runtime do Rive fica num pedaço só desta tela: é quase meio megabyte
        // de JS mais o .wasm, e nenhuma outra tela da web desenha Rive. O import
        // passa por `rive-runtime` de propósito, e não pelo pacote: o porquê
        // está lá.
        const [runtime, file] = await Promise.all([
            import('src/app/shared/services/rive-runtime'),
            fetch('/rive/document-stack.riv').then(response => response.arrayBuffer()),
        ]).catch(() => [null, null] as const);

        if (this._destroyed || !runtime || !file) return;

        const { Rive, RuntimeLoader, Layout, Fit, Alignment } = runtime;

        // Sem isto o runtime busca o .wasm no CDN da Rive, e o mesmo vale para o
        // `rive_fallback.wasm`, que ele tenta sozinho quando o primeiro não
        // compila (motor antigo, sem SIMD). Os dois vêm do próprio servidor (ver
        // `assets` no angular.json): a tela de abertura não depende de internet
        // de terceiro.
        RuntimeLoader.setWasmUrl('rive.wasm');
        RuntimeLoader.setWasmFallbackUrl('rive_fallback.wasm');

        const canvas = this._stage().nativeElement;

        this._rive = new Rive({
            buffer: paintDocumentStack(file),
            canvas,
            autoplay: true,
            stateMachine: STATE_MACHINE,
            layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
            onLoad: () => {
                this._followCanvasSize(canvas);
                this.drawing.set(true);
                this._cycleSheets();
            },
            // Arquivo que não veio não segura nada: sem a pilha, esta tela é o
            // logotipo sozinho, e ela sai no tempo de sempre com o app atrás.
            onLoadError: () => null,
        });

        // Com a saída no primeiro dos dois, a tela costuma ir embora antes de o
        // arquivo chegar. Sem este `cleanup` o runtime seguiria desenhando num
        // canvas já solto do documento, pelo resto da sessão.
        if (this._destroyed) this._rive.cleanup();
    }

    /**
     * Mantém a superfície de desenho do tamanho do canvas.
     *
     * Um `resizeDrawingSurfaceToCanvas()` sozinho no `onLoad` não basta: o
     * arquivo às vezes chega antes de o CSS do componente entrar, e nesse
     * instante a caixa ainda mede zero por zero. A superfície nascia com zero
     * pixels e a pilha simplesmente não aparecia, sem erro nenhum no console.
     * O observador também cobre a janela sendo redimensionada com a tela no ar.
     */
    private _followCanvasSize(canvas: HTMLCanvasElement): void {
        const observer = new ResizeObserver(() => this._rive?.resizeDrawingSurfaceToCanvas());
        observer.observe(canvas);
        this._destroyRef.onDestroy(() => observer.disconnect());
    }

    /** Revezar as folhas é disparar o gatilho no compasso. */
    private _cycleSheets(): void {
        const trigger = this._rive
            ?.stateMachineInputs(STATE_MACHINE)
            ?.find(input => input.name === NEXT_SHEET_INPUT);

        if (!trigger) return;

        const tick = () => {
            if (this._destroyed || this.leaving()) return;
            trigger.fire();
            this._timers.push(setTimeout(tick, SHEET_INTERVAL_MS));
        };

        this._timers.push(setTimeout(tick, SHEET_INTERVAL_MS));
    }

    private _scheduleExit(delay: number): void {
        if (this._exitScheduled) return;
        this._exitScheduled = true;

        this._timers.push(
            setTimeout(() => {
                this.leaving.set(true);
                this._timers.push(setTimeout(() => this.done.emit(), EXIT_MS));
            }, delay),
        );
    }
}
